import { useAuth } from '../context/AuthContext'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { useToast } from '../components/ui/Toaster'
import { 
  Bell, LogOut, Shield, 
  Palette, Mail, Smartphone,
  ArrowLeft, User, Key, Trash2,
  Minimize, Share2,
  Users, 
  Flame, BarChart2
} from 'lucide-react'
import { clsx } from 'clsx'
import { useState, useEffect } from 'react'

interface SettingsState {
  // Account
  displayName: string
  username: string
  email: string
  // Preferences
  darkMode: 'light' | 'dark' | 'system'
  autoShare: boolean
  hapticFeedback: boolean
  reducedMotion: boolean
  autoSubmit: boolean
  // Notifications
  dailyReminder: boolean
  dailyReminderTime: string
  duelInvites: boolean
  streakAlerts: boolean
  weeklyDigest: boolean
  // Privacy
  showProfile: boolean
  showStreak: boolean
  showStats: boolean
  allowDuels: boolean
  // Security
  twoFactorEnabled: boolean
}

interface SettingsTabProps {
  settings: SettingsState
  setSettings: React.Dispatch<React.SetStateAction<SettingsState>>
}

export default function Settings() {
  const { profile, user, signOut } = useAuth()
  const { toast } = useToast()
  const [activeSection, setActiveSection] = useState<'account' | 'preferences' | 'notifications' | 'privacy' | 'danger'>('account')

  const sections = [
    { id: 'account' as const, label: 'Account', icon: User, desc: 'Profile, username, email' },
    { id: 'preferences' as const, label: 'Preferences', icon: Palette, desc: 'Theme, language, gameplay' },
    { id: 'notifications' as const, label: 'Notifications', icon: Bell, desc: 'Daily reminders, duel invites' },
    { id: 'privacy' as const, label: 'Privacy & Security', icon: Shield, desc: 'Data, sessions, 2FA' },
    { id: 'danger' as const, label: 'Danger Zone', icon: Trash2, desc: 'Delete account, reset data' },
  ]

  const [settings, setSettings] = useState({
    // Account
    displayName: profile?.display_name || '',
    username: profile?.username || '',
    email: user?.email || '',
    // Preferences
    darkMode: 'system' as 'light' | 'dark' | 'system',
    autoShare: false,
    hapticFeedback: true,
    reducedMotion: false,
    autoSubmit: false,
    // Notifications
    dailyReminder: true,
    dailyReminderTime: '09:00',
    duelInvites: true,
    streakAlerts: true,
    weeklyDigest: false,
    // Privacy
    showProfile: true,
    showStreak: true,
    showStats: true,
    allowDuels: true,
    // Security
    twoFactorEnabled: false,
  })

  // Load settings from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('overlap-settings')
    if (saved) {
      try {
        setSettings(JSON.parse(saved))
      } catch (e) {
        console.error('Failed to parse settings:', e)
      }
    }
  }, [])

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 lg:py-10 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-surface-900 dark:text-surface-100">Settings</h1>
          <p className="text-surface-500 dark:text-surface-400">Manage your Overlap experience</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <aside className="lg:col-span-1">
          <Card>
            <CardContent className="pt-4 pb-2">
              <nav className="space-y-1">
                {sections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={clsx(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all',
                      activeSection === section.id
                        ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400'
                        : 'text-surface-600 hover:text-surface-900 hover:bg-surface-100 dark:text-surface-400 dark:hover:text-surface-100 dark:hover:bg-surface-800'
                    )}
                  >
                    <section.icon className="w-5 h-5 flex-shrink-0" />
                    <div className="flex-1 min-w-0 text-left">
                      <p className="font-medium truncate">{section.label}</p>
                      <p className="text-xs truncate opacity-70">{section.desc}</p>
                    </div>
                  </button>
                ))}
              </nav>
            </CardContent>
          </Card>

          {/* Version info */}
          <div className="mt-4 text-center text-xs text-surface-400">
            <p>Overlap v1.0.0</p>
            <p>Built with React + Supabase</p>
          </div>
        </aside>

        {/* Content */}
        <main className="lg:col-span-3 space-y-6">
          {activeSection === 'account' && <AccountSettings settings={settings} setSettings={setSettings} />}
          {activeSection === 'preferences' && <PreferencesSettings settings={settings} setSettings={setSettings} />}
          {activeSection === 'notifications' && <NotificationsSettings settings={settings} setSettings={setSettings} />}
          {activeSection === 'privacy' && <PrivacySettings settings={settings} setSettings={setSettings} />}
          {activeSection === 'danger' && <DangerZone onSignOut={signOut} />}
        </main>
      </div>
    </div>
  )
}

function AccountSettings({ settings, setSettings }: SettingsTabProps) {
  return (
    <Card>
      <CardContent className="pt-6 space-y-6">
        <div>
          <h2 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-1">Account</h2>
          <p className="text-surface-500 dark:text-surface-400">Manage your profile and credentials</p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-surface-50 dark:bg-surface-900/50 rounded-xl">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center text-white font-bold text-2xl">
              {settings.displayName?.[0]?.toUpperCase() || settings.email?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1">
              <p className="font-semibold text-surface-900 dark:text-surface-100">{settings.displayName || 'Player'}</p>
              <p className="text-sm text-surface-500">@{settings.username || 'username'}</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label="Display Name"
              value={settings.displayName}
              onChange={(e) => setSettings({ ...settings, displayName: e.target.value })}
              placeholder="Your name"
            />
            <div className="flex items-center gap-2">
              <span className="text-surface-500">@</span>
              <Input
                label="Username"
                value={settings.username}
                onChange={(e) => setSettings({ ...settings, username: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })}
                placeholder="username"
              />
            </div>
          </div>

          <Input
            label="Email"
            type="email"
            value={settings.email}
            onChange={(e) => setSettings({ ...settings, email: e.target.value })}
            disabled
            helperText="Email changes require verification"
          />

          <Button onClick={() => {}} className="w-full sm:w-auto">
            Save Changes
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function PreferencesSettings({ settings, setSettings }: SettingsTabProps) {
  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6 space-y-6">
          <div>
            <h2 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-1">Appearance</h2>
            <p className="text-surface-500 dark:text-surface-400">Customize how Overlap looks</p>
          </div>

          <SettingRow
            icon={Palette}
            label="Theme"
            desc="Choose your preferred color scheme"
            action={
              <select
                value={settings.darkMode}
                onChange={(e) => setSettings({ ...settings, darkMode: e.target.value as 'light' | 'dark' | 'system' })}
                className="input-field w-auto"
              >
                <option value="system">System</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            }
          />

          <SettingRow
            icon={Minimize}
            label="Reduced Motion"
            desc="Minimize animations and transitions"
            action={
              <Toggle checked={settings.reducedMotion} onChange={(v) => setSettings({ ...settings, reducedMotion: v })} />
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6 space-y-6">
          <div>
            <h2 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-1">Gameplay</h2>
            <p className="text-surface-500 dark:text-surface-400">Customize your playing experience</p>
          </div>

          <SettingRow
            icon={Share2}
            label="Auto-share Results"
            desc="Automatically copy share card to clipboard after games"
            action={
              <Toggle checked={settings.autoShare} onChange={(v) => setSettings({ ...settings, autoShare: v })} />
            }
          />

          <SettingRow
            icon={Smartphone}
            label="Haptic Feedback"
            desc="Vibrate on slider interactions (mobile)"
            action={
              <Toggle checked={settings.hapticFeedback} onChange={(v) => setSettings({ ...settings, hapticFeedback: v })} />
            }
          />
        </CardContent>
      </Card>
    </div>
  )
}

function NotificationsSettings({ settings, setSettings }: SettingsTabProps) {
  return (
    <Card>
      <CardContent className="pt-6 space-y-6">
        <div>
          <h2 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-1">Notifications</h2>
          <p className="text-surface-500 dark:text-surface-400">Choose what you want to be notified about</p>
        </div>

        <div className="space-y-4">
          <SettingRow
            icon={Bell}
            label="Daily Reminder"
            desc="Get reminded to play your daily Overlap"
            action={
              <Toggle checked={settings.dailyReminder} onChange={(v) => setSettings({ ...settings, dailyReminder: v })} />
            }
          />
          
          {settings.dailyReminder && (
            <div className="ml-10 flex items-center gap-3">
              <label className="text-sm text-surface-500">Time</label>
              <input
                type="time"
                value={settings.dailyReminderTime}
                onChange={(e) => setSettings({ ...settings, dailyReminderTime: e.target.value })}
                className="input-field w-auto"
              />
            </div>
          )}

          <SettingRow
            icon={Users}
            label="Duel Invites"
            desc="Get notified when someone challenges you"
            action={
              <Toggle checked={settings.duelInvites} onChange={(v) => setSettings({ ...settings, duelInvites: v })} />
            }
          />

          <SettingRow
            icon={Flame}
            label="Streak Alerts"
            desc="Reminders when your streak is at risk"
            action={
              <Toggle checked={settings.streakAlerts} onChange={(v) => setSettings({ ...settings, streakAlerts: v })} />
            }
          />

          <SettingRow
            icon={Mail}
            label="Weekly Digest"
            desc="Summary of your week's Overlap activity"
            action={
              <Toggle checked={settings.weeklyDigest} onChange={(v) => setSettings({ ...settings, weeklyDigest: v })} />
            }
          />
        </div>
      </CardContent>
    </Card>
  )
}

function PrivacySettings({ settings, setSettings }: SettingsTabProps) {
  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6 space-y-6">
          <div>
            <h2 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-1">Profile Visibility</h2>
            <p className="text-surface-500 dark:text-surface-400">Control what others can see</p>
          </div>

          <SettingRow
            icon={User}
            label="Show Profile"
            desc="Allow others to view your profile page"
            action={
              <Toggle checked={settings.showProfile} onChange={(v) => setSettings({ ...settings, showProfile: v })} />
            }
          />

          <SettingRow
            icon={Flame}
            label="Show Streak"
            desc="Display your current streak on your profile"
            action={
              <Toggle checked={settings.showStreak} onChange={(v) => setSettings({ ...settings, showStreak: v })} />
            }
          />

          <SettingRow
            icon={BarChart2}
            label="Show Stats"
            desc="Display your game statistics publicly"
            action={
              <Toggle checked={settings.showStats} onChange={(v) => setSettings({ ...settings, showStats: v })} />
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6 space-y-6">
          <div>
            <h2 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-1">Gameplay Privacy</h2>
            <p className="text-surface-500 dark:text-surface-400">Control who can interact with you</p>
          </div>

          <SettingRow
            icon={Users}
            label="Allow Duels"
            desc="Let friends challenge you to duels"
            action={
              <Toggle checked={settings.allowDuels} onChange={(v) => setSettings({ ...settings, allowDuels: v })} />
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6 space-y-6">
          <div>
            <h2 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-1">Data & Security</h2>
            <p className="text-surface-500 dark:text-surface-400">Manage your data and sessions</p>
          </div>

          <div className="space-y-3">
            <Button variant="secondary" className="w-full sm:w-auto justify-start gap-2">
              <Mail className="w-4 h-4" />
              Export My Data
            </Button>
            <Button variant="secondary" className="w-full sm:w-auto justify-start gap-2">
              <Shield className="w-4 h-4" />
              Manage Sessions
            </Button>
            <Button variant="secondary" className="w-full sm:w-auto justify-start gap-2">
              <Key className="w-4 h-4" />
              Change Password
            </Button>
            <Button variant="secondary" className="w-full sm:w-auto justify-start gap-2">
              <Smartphone className="w-4 h-4" />
              Enable 2FA
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function DangerZone({ onSignOut }: { onSignOut: () => void }) {
  const [confirmDelete, setConfirmDelete] = useState(false)

  return (
    <Card className="border-red-200 dark:border-red-900/30">
      <CardContent className="pt-6 space-y-6">
        <div>
          <h2 className="text-xl font-bold text-red-600 dark:text-red-400 mb-1">Danger Zone</h2>
          <p className="text-surface-500 dark:text-surface-400">Irreversible actions. Proceed with caution.</p>
        </div>

        <div className="border-t border-surface-200 dark:border-surface-700 pt-6 space-y-6">
          <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-xl">
            <div>
              <p className="font-semibold text-red-600 dark:text-red-400">Sign Out</p>
              <p className="text-sm text-surface-500">Sign out of your account on this device</p>
            </div>
            <Button variant="secondary" onClick={onSignOut}>
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-xl">
            <div>
              <p className="font-semibold text-red-600 dark:text-red-400">Delete Account</p>
              <p className="text-sm text-surface-500">Permanently delete your account and all data</p>
            </div>
            <Button variant="ghost" onClick={() => setConfirmDelete(true)} className="text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30">
              Delete Account
            </Button>
          </div>
        </div>

        {confirmDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <Card className="max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-red-600 dark:text-red-400 mb-2">Delete Account?</h3>
              <p className="text-surface-600 dark:text-surface-400 mb-6">
                This action cannot be undone. All your streaks, stats, duels, and submissions will be permanently deleted.
              </p>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setConfirmDelete(false)} className="flex-1">
                  Cancel
                </Button>
                <Button variant="danger" onClick={() => { /* delete account */ setConfirmDelete(false) }} className="flex-1">
                  Delete Forever
                </Button>
              </div>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
interface SettingRowProps {
  icon: React.ElementType
  label: string
  desc: string
  action: React.ReactNode
}

function SettingRow({ icon: Icon, label, desc, action }: SettingRowProps) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3 flex-1">
        <Icon className="w-5 h-5 text-surface-400" />
        <div>
          <p className="font-medium text-surface-900 dark:text-surface-100">{label}</p>
          <p className="text-sm text-surface-500">{desc}</p>
        </div>
      </div>
      <div>{action}</div>
    </div>
  )
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative w-11 h-6 rounded-full transition-colors',
        checked ? 'bg-brand-500' : 'bg-surface-300 dark:bg-surface-600'
      )}
      role="switch"
      aria-checked={checked}
    >
      <span className={clsx(
        'absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
        checked ? 'translate-x-5' : 'translate-x-0.5'
      )} />
    </button>
  )
}